import { Controller } from "@hotwired/stimulus"

// One instance per day row in the location operating-hours form. The "Open"
// toggle drives the hidden `closed` field. An open day with no times set reads
// as "Call for hours" with a "Set time" button; pressing it reveals the two
// time dropdowns. A closed day swaps everything for a muted "Closed" label.
// Inactive dropdowns are disabled so they submit no stale times.
//
// The dropdowns are ordered open (first) then close (second). When either
// changes, options that would make the day close at or before it opens are
// disabled, keeping the close time strictly after the open time.
//
// Usage:
//   <div data-controller="schedule-row">
//     <input type="hidden" data-schedule-row-target="closedField">
//     <input type="checkbox" data-schedule-row-target="openToggle"
//            data-action="change->schedule-row#toggle">
//     <div data-schedule-row-target="times">
//       <select data-schedule-row-target="time">…</select>
//       <select data-schedule-row-target="time">…</select>
//     </div>
//     <div data-schedule-row-target="callForHours">
//       Call for hours
//       <button data-action="schedule-row#setTime">Set time</button>
//     </div>
//     <span data-schedule-row-target="closedLabel" class="hidden">Closed</span>
//   </div>
export default class extends Controller {
  static targets = ["openToggle", "closedField", "times", "callForHours", "closedLabel", "time"]

  declare readonly openToggleTarget: HTMLInputElement
  declare readonly hasOpenToggleTarget: boolean
  declare readonly closedFieldTarget: HTMLInputElement
  declare readonly hasClosedFieldTarget: boolean
  declare readonly timesTarget: HTMLElement
  declare readonly hasTimesTarget: boolean
  declare readonly callForHoursTarget: HTMLElement
  declare readonly hasCallForHoursTarget: boolean
  declare readonly closedLabelTarget: HTMLElement
  declare readonly hasClosedLabelTarget: boolean
  declare readonly timeTargets: HTMLSelectElement[]

  // Whether the time dropdowns are revealed (vs. the "Call for hours" prompt).
  private revealTimes = false

  connect(): void {
    if (!this.hasOpenToggleTarget) return
    this.revealTimes = this.hasTimeValues()
    this.render()
  }

  toggle(): void {
    // Re-opening a day falls back to the call-for-hours prompt unless it already
    // carries times.
    this.revealTimes = this.hasTimeValues()
    this.render()
  }

  setTime(): void {
    this.revealTimes = true
    this.render()
    this.timeTargets[0]?.focus()
  }

  // Fired when either time dropdown changes. If the pair is now invalid
  // (close <= open), clear the partner the user did not just touch so their
  // latest pick stands, then re-disable the out-of-range options on both.
  timeChanged(event: Event): void {
    const [opensSelect, closesSelect] = this.timeTargets
    if (!opensSelect || !closesSelect) return

    if (opensSelect.value && closesSelect.value && opensSelect.value >= closesSelect.value) {
      const partner = event.target === opensSelect ? closesSelect : opensSelect
      partner.value = ""
    }

    this.constrainTimes()
  }

  private get open(): boolean {
    return this.openToggleTarget.checked
  }

  private hasTimeValues(): boolean {
    return this.timeTargets.some((time) => time.value !== "")
  }

  private render(): void {
    const open = this.open
    const showingTimes = open && this.revealTimes
    const showingCallForHours = open && !showingTimes

    if (this.hasClosedFieldTarget) this.closedFieldTarget.value = open ? "0" : "1"
    if (this.hasTimesTarget) this.timesTarget.classList.toggle("hidden", !showingTimes)
    if (this.hasCallForHoursTarget) this.callForHoursTarget.classList.toggle("hidden", !showingCallForHours)
    if (this.hasClosedLabelTarget) this.closedLabelTarget.classList.toggle("hidden", open)
    this.timeTargets.forEach((time) => (time.disabled = !showingTimes))
    if (showingTimes) this.constrainTimes()
  }

  // Disables out-of-range options so the close time stays after the open time:
  // close options at/before the chosen open are disabled, and open options
  // at/after the chosen close are disabled. The blank option stays selectable.
  private constrainTimes(): void {
    const [opensSelect, closesSelect] = this.timeTargets
    if (!opensSelect || !closesSelect) return

    const opensValue = opensSelect.value
    const closesValue = closesSelect.value

    for (const option of Array.from(closesSelect.options)) {
      option.disabled = option.value !== "" && opensValue !== "" && option.value <= opensValue
    }
    for (const option of Array.from(opensSelect.options)) {
      option.disabled = option.value !== "" && closesValue !== "" && option.value >= closesValue
    }
  }
}
